import { useState, useMemo, type ChangeEvent } from "react";
import type { OpeningListItem } from "~/lib/objects";
import OpeningOrientationSquare from "~/components/openings/opening-orientation-square";
import { formatOpeningMetrics } from "~/utils/openingMetrics";
import { Input } from "~/components/ui/input";
import {
  filterByWordPrefixes,
  hasWordPrefixQuery,
} from "~/lib/search/word-prefix-search";
import type { OpeningTitleQueryStore } from "~/components/openings/use-opening-title-query-store";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";

type OpeningListProps = {
  problemSetId: number;
  openings: readonly OpeningListItem[];
  loaded: boolean;
  queryStore: OpeningTitleQueryStore;
  onOpeningClick: (opening: OpeningListItem) => void;
};

export default function OpeningList({
  problemSetId,
  openings,
  loaded,
  queryStore,
  onOpeningClick,
}: OpeningListProps) {
  const [searchTitle, setSearchTitle] = useState(() =>
    queryStore.read(problemSetId),
  );
  const filteredOpenings = useMemo(
    () =>
      filterByWordPrefixes(openings, searchTitle, (opening) => opening.title),
    [openings, searchTitle],
  );
  const showNoResults =
    loaded && hasWordPrefixQuery(searchTitle) && filteredOpenings.length === 0;

  const handleSearchTitleChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    const query = event.target.value;
    setSearchTitle(query);
    queryStore.write(problemSetId, query);
  };

  return (
    <>
      <Input
        type="search"
        aria-label="Поиск по названию дебюта"
        placeholder="Фильтр по названию"
        value={searchTitle}
        onChange={handleSearchTitleChange}
        className="mb-4 w-full flex-shrink-0"
      />
      <div className="min-h-0 flex-1 overflow-hidden rounded-md border">
        <div className="h-full [&>div]:h-full [&>div]:overflow-y-auto [&>div]:overflow-x-hidden">
          <Table aria-label="Список дебютов" className="table-fixed">
            <colgroup>
              <col className="w-6" />
              <col />
              <col className="w-40" />
            </colgroup>
            <TableHeader className="sticky top-0 z-10 border-b bg-background">
              <TableRow>
                <TableHead className="w-6 whitespace-nowrap bg-background">
                  <span className="sr-only">Ориентация</span>
                </TableHead>
                <TableHead className="min-w-0 bg-background">
                  Название
                </TableHead>
                <TableHead className="w-40 whitespace-nowrap bg-background">
                  Метрики
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showNoResults && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="py-6 text-center text-muted-foreground"
                  >
                    Дебюты не найдены
                  </TableCell>
                </TableRow>
              )}
              {filteredOpenings.map((opening) => (
                <TableRow key={`${opening.problemSetId}-${opening.problemId}`}>
                  <TableCell className="w-6 whitespace-nowrap">
                    <OpeningOrientationSquare
                      orientation={opening.orientation}
                    />
                  </TableCell>
                  <TableCell className="min-w-0 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => onOpeningClick(opening)}
                      className="block min-w-0 whitespace-normal break-words text-left text-sm leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {opening.title}
                    </button>
                  </TableCell>
                  <TableCell className="w-40 whitespace-nowrap">
                    {formatOpeningMetrics(opening.openingMetrics)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}
